import { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useSupabaseQuery } from '../../hooks/useSupabaseQuery';
import { PageHeader } from '../../components/ui/PageHeader';
import { EmptyState } from '../../components/ui/EmptyState';
import { isStaff } from '../../lib/roles';
import type { ClienteRow } from '../../types/database.types';

/**
 * Desde `schema_profesional_ventas_agenda.sql` (pedido explícito del
 * usuario, ago-sep/2026), una profesional ve aquí TODAS las clientas del
 * salón -- no solo las que ya atendió -- porque necesita poder elegir
 * cualquiera al agendar o venderle (ver NuevaCitaForm.tsx / VentaForm.tsx).
 * También puede CREAR una clienta nueva ella misma ("Nueva clienta" ya no
 * se oculta para ese rol). Lo único que sigue siendo exclusivo de
 * recepción/administrador (DAF v2, sección 5.2) es EDITAR una clienta ya
 * existente -- el link "Editar" solo aparece para staff, y RLS
 * (`clientes_staff_all`) es la barrera real, esto solo evita que el link
 * la lleve a un formulario que de todas formas le va a rechazar el
 * guardado (ver también el guard en ClientaForm.tsx).
 */
export function ClientasList() {
  const { perfil } = useAuth();
  const staff = isStaff(perfil?.rol);
  const puedeCrear = staff || perfil?.rol === 'profesional';
  const [busqueda, setBusqueda] = useState('');

  const { data, loading, error } = useSupabaseQuery<ClienteRow[]>(async () => {
    let query = supabase.from('clientes').select('*').neq('estado', 'anulado');

    if (busqueda.trim()) {
      query = query.or(`nombre.ilike.%${busqueda}%,telefono.ilike.%${busqueda}%`);
    }

    return query.order('nombre', { ascending: true });
  }, [busqueda]);

  return (
    <div>
      <PageHeader
        title="Clientas"
        description="Historial y datos de contacto de las clientas del salón"
        actions={
          puedeCrear ? (
            <Link to="/admin/clientas/nueva" className="btn-primary">
              Nueva clienta
            </Link>
          ) : undefined
        }
      />

      <div className="mb-4">
        <input
          className="input-field max-w-xs"
          placeholder="Buscar por nombre o teléfono…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
      </div>

      {loading && <p className="text-sm text-onyx/50">Cargando…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {data && data.length === 0 && (
        <EmptyState
          title="No hay clientas registradas"
          description={puedeCrear ? 'Crea la primera desde el botón de arriba.' : 'Todavía no hay clientas en el salón.'}
        />
      )}

      {data && data.length > 0 && (
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-onyx/10 text-left text-onyx/50">
                <th className="px-4 py-3 font-medium">Nombre</th>
                <th className="px-4 py-3 font-medium">Teléfono</th>
                <th className="px-4 py-3 font-medium">Correo</th>
                <th className="px-4 py-3 font-medium">Estado</th>
                {staff && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody>
              {data.map((c) => (
                <tr key={c.id} className="border-b border-onyx/5 last:border-0 hover:bg-onyx/[0.02]">
                  <td className="px-4 py-3 font-medium text-onyx">
                    {c.nombre} {c.apellido ?? ''}
                  </td>
                  <td className="px-4 py-3 text-onyx/70">{c.telefono ?? '—'}</td>
                  <td className="px-4 py-3 text-onyx/70">{c.email ?? '—'}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`badge ${
                        c.estado === 'activo' ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'
                      }`}
                    >
                      {c.estado}
                    </span>
                  </td>
                  {staff && (
                    <td className="px-4 py-3 text-right">
                      <Link to={`/admin/clientas/${c.id}`} className="text-gold-deep hover:underline">
                        Editar
                      </Link>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
